import React from "react";
import {
  Box,
  Button,
  Circle,
  Flex,
  Icon,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FiShield } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { auth } from "../../services/api";
import { HeroTexture } from "../common/HeroTexture";
import { SejamSyncModal } from "../SejamSyncModal";

const MotionBox = motion(Box);

const valueOrDash = (value?: string | null) => (value ? String(value) : "-");

const SummaryCard = ({
  label,
  value,
  helper,
}: {
  label: string;
  value: string;
  helper: string;
}) => {
  const cardBg = useColorModeValue("whiteAlpha.900", "whiteAlpha.90");
  const borderColor = useColorModeValue("blackAlpha.100", "whiteAlpha.120");
  const labelColor = useColorModeValue("gray.500", "gray.400");
  const valueColor = useColorModeValue("gray.900", "white");
  const helperColor = useColorModeValue("gray.600", "gray.300");

  return (
    <Box
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      rounded="2xl"
      p={4}
      minH="120px"
      backdropFilter="blur(10px)"
    >
      <VStack align="flex-start" spacing={2}>
        <Text fontSize="xs" color={labelColor}>
          {label}
        </Text>
        <Text
          fontSize={{ base: "2xl", md: "3xl" }}
          fontWeight="900"
          color={valueColor}
        >
          {value}
        </Text>
        <Text fontSize="sm" color={helperColor} lineHeight="1.9">
          {helper}
        </Text>
      </VStack>
    </Box>
  );
};

type SejamSyncRequiredSectionProps = {
  identifier?: string;
  identifierLabel?: string;
  title?: string;
  description?: string;
  minHeight?: string | number;
  onSynced?: () => Promise<unknown> | unknown;
};

export const SejamSyncRequiredSection: React.FC<
  SejamSyncRequiredSectionProps
> = ({
  identifier,
  identifierLabel = "کد ملی",
  title = "همگام‌سازی با سجام لازم است",
  description = "برای نمایش کامل اطلاعات هویتی، بانکی و امکانات داشبورد، ابتدا باید اطلاعات حساب شما با سجام همگام شود تا داده‌های قابل اتکا و به‌روز در پروفایل ثبت شوند.",
  minHeight = "100%",
  onSynced,
}) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { refreshCurrentUser } = useAuth();
  const [isSendingSyncOtp, setIsSendingSyncOtp] = React.useState(false);

  const heroBg = useColorModeValue(
    "radial-gradient(circle at 85% 45%, rgba(249,168,166,0.45), transparent 50%), radial-gradient(circle at 15% 85%, rgba(249,168,166,0.45), transparent 50%)",
    "radial-gradient(circle at 85% 15%, rgba(173,11,6,0.22), transparent 50%), radial-gradient(circle at 15% 85%, rgba(173,11,6,0.22), transparent 50%)",
  );
  const heroOverlay = useColorModeValue(
    "linear-gradient(130deg, rgba(163,32,32,0.10), rgba(180,83,9,0.08))",
    "linear-gradient(130deg, rgba(163,32,32,0.18), rgba(180,83,9,0.12))",
  );
  const heroText = useColorModeValue("gray.900", "white");
  const heroMuted = useColorModeValue("gray.600", "gray.300");
  const emptyBorder = useColorModeValue("blackAlpha.100", "whiteAlpha.160");
  const subtleBadgeBg = useColorModeValue("white", "icon.bg");
  const subtleBadgeColor = useColorModeValue("brand.500", "icon.svg");
  const avatarRing = useColorModeValue("blackAlpha.100", "whiteAlpha.200");

  const handleStartSejamSync = React.useCallback(async () => {
    try {
      setIsSendingSyncOtp(true);
      await auth.requestSyncOtp();
      toast({
        title: "کد تایید ارسال شد",
        description: "کد تایید سجام برای شما ارسال شد.",
        status: "success",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
      onOpen();
    } catch (error: any) {
      toast({
        title: "ارسال کد انجام نشد",
        description:
          error?.response?.data?.message ||
          "امکان ارسال کد تایید سجام وجود ندارد.",
        status: "error",
        duration: 4000,
        position: "top",
        isClosable: true,
      });
    } finally {
      setIsSendingSyncOtp(false);
    }
  }, [onOpen, toast]);

  const handleSynced = React.useCallback(async () => {
    await refreshCurrentUser();
    if (onSynced) {
      await onSynced();
    }
  }, [onSynced, refreshCurrentUser]);

  return (
    <Box
      h="100%"
      overflowY="auto"
      px={{ base: 1, md: 2 }}
      py={{ base: 4, md: 8 }}
    >
      <Flex minH={minHeight} align="center" justify="center">
        <MotionBox
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.42, ease: "easeOut" }}
          w="100%"
          maxW="900px"
        >
          <Box
            bg={heroBg}
            rounded="3xl"
            p={{ base: 6, md: 10 }}
            color={heroText}
            overflow="hidden"
            position="relative"
            borderWidth="1px"
            borderColor={emptyBorder}
            _before={{
              content: '""',
              position: "absolute",
              inset: 0,
              background: heroOverlay,
              zIndex: 0,
            }}
          >
            <HeroTexture />
            <VStack position="relative" spacing={7}>
              <Circle
                size="82px"
                bg={subtleBadgeBg}
                borderWidth="1px"
                borderColor={avatarRing}
              >
                <Icon
                  as={FiShield as any}
                  boxSize={8}
                  color={subtleBadgeColor}
                />
              </Circle>

              <VStack spacing={3} textAlign="center">
                <Text fontSize={{ base: "2xl", md: "4xl" }} fontWeight="950">
                  {title}
                </Text>
                <Text
                  color={heroMuted}
                  fontSize={{ base: "sm", md: "md" }}
                  lineHeight="2.1"
                  maxW="640px"
                >
                  {description}
                </Text>
              </VStack>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="100%">
                <SummaryCard
                  label={identifierLabel}
                  value={valueOrDash(identifier)}
                  helper="شناسه اصلی حساب شما برای تطبیق اطلاعات"
                />
                <SummaryCard
                  label="وضعیت پروفایل"
                  value="ناقص"
                  helper="بدون همگام‌سازی، بخش زیادی از اطلاعات نمایش داده نمی‌شود"
                />
                <SummaryCard
                  label="گام بعدی"
                  value="دریافت کد"
                  helper="با یک تایید پیامکی، همگام‌سازی پروفایل آغاز می‌شود"
                />
              </SimpleGrid>

              <Button
                size="lg"
                borderRadius="full"
                px={8}
                onClick={handleStartSejamSync}
                isDisabled={!identifier}
                isLoading={isSendingSyncOtp}
                loadingText="در حال ارسال کد..."
              >
                همگام‌سازی با سجام
              </Button>
            </VStack>
          </Box>
        </MotionBox>
      </Flex>

      <SejamSyncModal
        isOpen={isOpen}
        onClose={onClose}
        onSynced={handleSynced}
      />
    </Box>
  );
};
